import React, { useEffect, useRef } from "react";

// Miniature of the landing-page BrainCanvas: a slowly spinning point-cloud
// sphere in graphite ink with an occasional amber "synapse" flash between two
// points. ~160 points on one small canvas, one rAF loop, no data file, so it
// is cheap enough for buttons and cards. Honors prefers-reduced-motion.
//
// Props: size (px, default 48), tone "ink" (paper backgrounds) | "paper"
// (dark backgrounds) | "current" (inherits CSS text color, for buttons),
// className. Below 28px it drops to 48 points so the sphere stays legible.
const N_FULL = 160;
const N_SMALL = 48;
const INK = "59,55,51";        // plate.graphite
const PAPER = "250,247,241";   // plate.paper
const AMBER_HALO = "239,196,120";
const AMBER_CORE = "227,176,59"; // plate.accent
const FLASH_MS = 1400;
const REV_MS = 6000;

// "rgb(r, g, b)" from the computed text color -> "r,g,b"; falls back to ink.
function currentColor(el) {
    const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(getComputedStyle(el).color || "");
    return m ? `${m[1]},${m[2]},${m[3]}` : INK;
}

// Drop-in for the old 16px button spinner: <ButtonParticleLoader /> inherits
// the button's text color, so it works on ink and paper buttons alike.
export function ButtonParticleLoader(props) {
    return <ParticleLoader size={16} tone="current" {...props} />;
}

export default function ParticleLoader({ size = 48, tone = "ink", className = "" }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas && canvas.getContext && canvas.getContext("2d");
        if (!ctx) return undefined;

        const DPR = Math.min(window.devicePixelRatio || 1, 2);
        const px = size * DPR;
        canvas.width = canvas.height = px;
        const half = px / 2;
        const R = half * 0.78;
        const small = size < 28;
        const N = small ? N_SMALL : N_FULL;
        const dot = Math.max(small ? 0.9 : 0.6, px / 110);
        const color = tone === "paper" ? PAPER
            : tone === "current" ? currentColor(canvas)
            : INK;

        // Fibonacci sphere with a little jitter so it reads as a cloud, not a grid.
        const xs = new Float32Array(N), ys = new Float32Array(N), zs = new Float32Array(N);
        const golden = Math.PI * (3 - Math.sqrt(5));
        for (let i = 0; i < N; i++) {
            const y = 1 - (i / (N - 1)) * 2;
            const r = Math.sqrt(1 - y * y) * (0.85 + Math.random() * 0.15);
            const t = golden * i;
            xs[i] = Math.cos(t) * r;
            ys[i] = y * (0.85 + Math.random() * 0.15);
            zs[i] = Math.sin(t) * r;
        }
        const sx = new Float32Array(N), sy = new Float32Array(N), depth = new Float32Array(N);
        const tilt = 0.35, tsin = Math.sin(tilt), tcos = Math.cos(tilt);

        let flash = null; // { a, b, birth }
        let nextFlash = 0;

        function project(angle) {
            const sin = Math.sin(angle), cos = Math.cos(angle);
            for (let i = 0; i < N; i++) {
                const x = xs[i] * cos + zs[i] * sin;
                const z0 = -xs[i] * sin + zs[i] * cos;
                const y = ys[i] * tcos - z0 * tsin;
                const z = ys[i] * tsin + z0 * tcos;
                const p = 2.2 / (2.2 + z);
                sx[i] = half + x * R * p;
                sy[i] = half + y * R * p;
                depth[i] = (1 - z) / 2; // 0 far … 1 near
            }
        }

        function draw(angle, now) {
            ctx.clearRect(0, 0, px, px);
            project(angle);
            ctx.fillStyle = `rgb(${color})`;
            for (let i = 0; i < N; i++) {
                ctx.globalAlpha = 0.25 + 0.7 * depth[i];
                const r = dot * (0.6 + 0.8 * depth[i]);
                ctx.beginPath();
                ctx.arc(sx[i], sy[i], r, 0, 7);
                ctx.fill();
            }
            ctx.globalAlpha = 1;

            if (flash && now - flash.birth > FLASH_MS) flash = null;
            if (!flash && now > nextFlash) {
                const a = (Math.random() * N) | 0;
                let b = (Math.random() * N) | 0;
                if (b === a) b = (a + 1) % N;
                flash = { a, b, birth: now };
                nextFlash = now + 600 + Math.random() * 900;
            }
            if (!flash) return;
            const t = (now - flash.birth) / FLASH_MS;
            const fade = t < 0.15 ? t / 0.15 : (1 - t) / 0.85;
            const reach = Math.min(1, t / 0.4); // line grows from a to b
            const ax = sx[flash.a], ay = sy[flash.a], bx = sx[flash.b], by = sy[flash.b];
            ctx.strokeStyle = `rgba(${AMBER_HALO},${(0.7 * fade).toFixed(3)})`;
            ctx.lineWidth = dot * 1.4;
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(ax + (bx - ax) * reach, ay + (by - ay) * reach);
            ctx.stroke();
            ctx.strokeStyle = `rgba(${AMBER_CORE},${fade.toFixed(3)})`;
            ctx.lineWidth = dot * 0.5;
            ctx.stroke();
            [[ax, ay, 1], [bx, by, reach]].forEach(([x, y, lit]) => {
                if (lit < 1) return;
                ctx.fillStyle = `rgba(${AMBER_HALO},${(0.6 * fade).toFixed(3)})`;
                ctx.beginPath(); ctx.arc(x, y, dot * 3, 0, 7); ctx.fill();
                ctx.fillStyle = `rgba(${AMBER_CORE},${fade.toFixed(3)})`;
                ctx.beginPath(); ctx.arc(x, y, dot * 1.5, 0, 7); ctx.fill();
            });
        }

        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            draw(0.6, 0);
            return undefined;
        }
        let rafId = 0;
        const start = performance.now();
        const tick = (now) => {
            draw(((now - start) / REV_MS) * Math.PI * 2, now);
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [size, tone]);

    return (
        <span
            role="status"
            aria-label="Loading"
            className={"inline-flex items-center justify-center " + className}
            style={{ width: size, height: size }}
        >
            <canvas ref={canvasRef} style={{ width: size, height: size }} aria-hidden="true" />
        </span>
    );
}
